import { tokens } from '@elka/theme';
import { ArrowLeft } from 'lucide-react';
import type { NextAction } from '../lib/nextAction';

interface NextActionChipProps {
  action: NextAction;
  onClick?: () => void;
}

function toneColors(tone: NextAction['tone']) {
  switch (tone) {
    case 'hot':
      return {
        bg: '#FFFBEB',
        fg: '#B45309',
        border: '#FDE68A',
        urgencyFg: '#92400E',
      };
    case 'success':
      return {
        bg: '#F0FDF4',
        fg: '#166534',
        border: '#BBF7D0',
        urgencyFg: '#15803D',
      };
    case 'info':
      return {
        bg: '#EFF6FF',
        fg: '#1E40AF',
        border: '#BFDBFE',
        urgencyFg: '#2563EB',
      };
    case 'neutral':
    default:
      return {
        bg: 'white',
        fg: tokens.colors.pricing.navy,
        border: tokens.colors.pricing.line,
        urgencyFg: tokens.colors.pricing.textMute,
      };
  }
}

/**
 * "Next action" chip — one per deal row. Clicking jumps directly to the relevant
 * screen for the action, not to the deal's overview page.
 */
export function NextActionChip({ action, onClick }: NextActionChipProps) {
  const palette = toneColors(action.tone);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className="inline-flex items-center gap-1.5 text-[11px] font-bold transition-[box-shadow,transform] hover:shadow-sm active:scale-[0.96]"
      style={{
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        padding: '5px 10px',
        borderRadius: 999,
      }}
    >
      <span>{action.label}</span>
      {action.urgency && (
        <span
          className="text-[10px] font-semibold"
          style={{ color: palette.urgencyFg, opacity: 0.85 }}
        >
          {action.urgency}
        </span>
      )}
      <ArrowLeft size={11} style={{ marginInlineStart: 2 }} />
    </button>
  );
}
